import ExpandMore from '@mui/icons-material/ExpandMore'
import History from '@mui/icons-material/History'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import dayjs from 'dayjs'
import { forwardRef, useCallback, useEffect, useImperativeHandle, memo, useMemo, useRef, useState } from 'react'
import PatientHistoryTipTapEditor from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/PatientHistoryTipTapEditor'
import Print from '@mui/icons-material/Print'
import EmrSectionDataHandler from 'src/mentor/components/EmrRelatedComponents/components/EmrSectionDataHandler'
import ImportPatientHistoryButton from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/ImportPatientHistoryButton'
import { StyledRTEContainer } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/styledComponents'
import { usePatientHistoryNotes } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/hooks/usePatientHistoryNotes'
import { useScreeningReportState } from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/hooks/useScreeningReportState'

// Read-only print indicator component
const PrintIndicator = memo(({ isPrintableOnRx }) => {
  if (!isPrintableOnRx) {
    return null
  }

  return (
    <Tooltip title="Included in printed prescription" arrow placement="top">
      <Chip
        icon={<Print sx={{ fontSize: '16px' }} />}
        label="On Rx"
        size="small"
        sx={{
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          color: '#059669',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          fontWeight: 600,
          fontSize: '11px',
          height: '24px',
          '& .MuiChip-icon': {
            color: '#059669',
          },
        }}
      />
    </Tooltip>
  )
})

PrintIndicator.displayName = 'PrintIndicator'

const PatientHistoryNotesDisplay = forwardRef(
  (
    {
      bookedEventInfo,
      screeningReportData,
      setScreeningReportData,
      screeningReportId,
      isFirstScreening = false,
      currentSectionData,
      setPatientHistoryNotes,
      patientHistoryEmrData,
      setPatientHistoryEmrData,
      screeningEmrSectionDataList,
      emrSectionDataList,
      setEmrSectionDataList,
      onOpenEmrSectionsEditor, // Callback to open EMR Sections Editor Dialog
      isPrintableOnRx = false, // Whether this section is printable on prescription (from EMR sections config)
    },
    componentRef,
  ) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const isTablet = useMediaQuery(theme.breakpoints.down('md'))
    
    const {
      patientHistoryData,
      setPatientHistoryData,
      allPatientHistoryData,
      updateOrCreatePatientHistoryData,
      importPatientHistoryNotesFromScreeningReport,
      savingRecord,
      loading,
    } = usePatientHistoryNotes(bookedEventInfo?.patientId, screeningReportId, bookedEventInfo?.id)
    // const { emrSections } = useEmrSections([], 'screening-report')

    const patientHistoryNotesSection = currentSectionData

    const { addToExportLog } = useScreeningReportState(screeningReportData, setScreeningReportData)
    // const subSections = currentEmrSectionResponse?.subSections

    function importPatientHistoryNotesWithScreeningEmrSectionData(screeningReportData) {
      importPatientHistoryNotesFromScreeningReport(screeningReportData)
      setEmrSectionDataList((prev) => [
        ...prev,
        ...(screeningEmrSectionDataList || []).filter(
          (s) => s.sectionCode === patientHistoryNotesSection?.defCode,
        ),
      ])
    }

    // Expose the importPatientHistoryNotesFromScreeningReport function to parent components
    useImperativeHandle(componentRef, () => ({
      importPatientHistoryNotesFromScreeningReport:
        !patientHistoryData || patientHistoryData?.length === 0
          ? importPatientHistoryNotesWithScreeningEmrSectionData
          : null,
      saveCurrentNotes: () => {
        handleSaveEdit()
      },
    }))

    const [initialPatientHistoryEditing, setInitialPatientHistoryEditing] = useState(null)

    const hasChanged = true
    // const hasChanged =
    //   initialPatientHistoryEditing?.content?.content !== patientHistoryEditing?.content?.content

    const handleFirstItemContentChange = async (newContent) => {
      setPatientHistoryData((prev) => {
        // If prev is null, create a new structure
        if (!prev) {
          return {
            content: {
              content: newContent,
            },
          }
        }
        // Otherwise, update the existing structure
        return {
          ...prev,
          content: {
            ...prev?.content,
            content: newContent,
          },
        }
      })

      // if (patientHistoryData?.[0] && updatePatientHistoryData) {
      //   try {
      //     const updatedItem = {
      //       ...patientHistoryData[0],
      //       content: newContent,
      //     }
      //     await updatePatientHistoryData(updatedItem)
      //   } catch (error) {
      //     console.error('Error updating first item content:', error)
      //   }
      // }
    }

    function handleResetNotes() {
      setPatientHistoryData(initialPatientHistoryEditing)
      setEmrSectionDataList(screeningEmrSectionDataList)
    }

    function handleSaveEdit() {
      if (patientHistoryData && hasChanged) {
        updateOrCreatePatientHistoryData(patientHistoryData, screeningReportId, bookedEventInfo?.id)
      }
    }

    async function handleImportNotes() {
      const result = await importPatientHistoryNotesWithScreeningEmrSectionData(screeningReportData)
      if (result.success) {
        addToExportLog({
          id: `${Math.random()}`,
          screeningReportId,
        })
      }
    }

    function handleContinueWithoutImport() {
      // Add entry to export log to indicate user chose to continue without import
      importPatientHistoryNotesWithScreeningEmrSectionData(null)
      addToExportLog({
        id: Date.now(),
        timestamp: new Date().toISOString(),
        action: 'continued_without_import',
        screeningReportId,
      })
    }

    useEffect(() => {
      if (patientHistoryData) {
        if (setPatientHistoryNotes) {
          // Use functional update to skip re-render when the data hasn't actually changed.
          // Two instances of this component exist on the page (one per PatientHistory EMR
          // section). React StrictMode also double-invokes mount effects. Without this guard,
          // 3-4 calls each create a new array causing 3-4 full PMP re-renders (~2 seconds).
          //
          // Guard rules:
          //  1. Skip entries with no ID – these are placeholder/unloaded states from the
          //     second component instance that fires before its data resolves.
          //  2. Use functional update with ID + length check so identical subsequent calls
          //     return the same array reference (no re-render).
          setPatientHistoryNotes((prev) => {
            // Rule 1: skip placeholder entries (no-ID state triggers no state change)
            if (!patientHistoryData?.id) return prev

            // Rule 2: skip if content hasn't changed
            const newAllLen = (allPatientHistoryData || []).length
            if (
              prev?.[0]?.id === patientHistoryData.id &&
              (prev?.length ?? 0) - 1 === newAllLen
            ) {
              return prev
            }
            return [patientHistoryData, ...(allPatientHistoryData || [])]
          })
        }
        setInitialPatientHistoryEditing(patientHistoryData)
      }
    }, [patientHistoryData, setPatientHistoryNotes, allPatientHistoryData])


    // // Show initial choice if no patient history data exists
    // if (!skipImportScreen) {
    //   if (!patientHistoryData || patientHistoryData.length === 0) {
    //     return (
    //       <Stack spacing={2}>
    //         <Typography variant="h6" sx={{ mb: 2 }}>
    //           Patient History Notes
    //         </Typography>
    //         <PatientHistoryInitialChoice
    //           onImportFromScreening={handleImportNotes}
    //           onContinue={handleContinueWithoutImport}
    //           isImporting={savingRecord}
    //         />
    //       </Stack>
    //     )
    //   }
    // }

    return (
      <>
        <Card
          sx={{
            mb: { xs: 2, sm: 3 },
            maxWidth: '1200px',
            width: '100%',
            background: 'linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%)',
            border: '1px solid rgba(67, 56, 202, 0.12)',
            borderRadius: { xs: '12px', sm: '16px' },
            boxShadow: { xs: '0 2px 8px rgba(67, 56, 202, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)', sm: '0 4px 20px rgba(67, 56, 202, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)' },
            overflow: 'visible',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: { xs: '3px', sm: '4px' },
              background: 'linear-gradient(90deg, rgba(67, 56, 202, 0.8) 0%, rgba(99, 102, 241, 0.8) 100%)',
              borderRadius: { xs: '12px 12px 0 0', sm: '16px 16px 0 0' },
            },
          }}
          className={isPrintableOnRx ? '' : 'hide-on-print'}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Header Section */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                mb: { xs: 2, sm: 2.5 },
                pb: { xs: 1.5, sm: 2 },
                borderBottom: '2px solid rgba(67, 56, 202, 0.1)',
                gap: { xs: 1.5, sm: 0 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                <Box
                  sx={{
                    width: { xs: '36px', sm: '40px' },
                    height: { xs: '36px', sm: '40px' },
                    borderRadius: { xs: '8px', sm: '10px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                    color: '#4338ca',
                    flexShrink: 0,
                  }}
                >
                  <History sx={{ fontSize: { xs: 20, sm: 22 } }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#1e293b',
                      fontSize: { xs: '18px', sm: '20px' },
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      letterSpacing: '-0.01em',
                      mb: 0.5,
                      lineHeight: 1.2,
                    }}
                  >
                    Patient History Notes
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontSize: { xs: '11px', sm: '12px' },
                      fontStyle: 'italic',
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    Document patient history and clinical notes
                  </Typography>
                </Box>
                {loading && (
                  <CircularProgress size={18} sx={{ color: '#4338ca', ml: { xs: 0.5, sm: 1 }, flexShrink: 0 }} />
                )}
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 1, sm: 1.5 },
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                mt: { xs: 0.5, sm: 0 },
              }}>
                <PrintIndicator isPrintableOnRx={isPrintableOnRx} />
                {!hasChanged && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size={isMobile ? 'small' : 'small'}
                    onClick={handleResetNotes}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      px: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '12px', sm: '14px' },
                    }}
                  >
                    Reset
                  </Button>
                )}
              </Box>
            </Box>

            {/* Editor Section */}
            <Box sx={{ position: 'relative' }}>
              <StyledRTEContainer style={{ position: 'relative' }}>
                <PatientHistoryTipTapEditor
                  key={`patient-history-editor-${patientHistoryData?.id || 'new'}-${patientHistoryData?.createdAt || patientHistoryData?.createdTime || ''}`}
                  value={patientHistoryData?.content?.content || ''}
                  onChange={(value) => handleFirstItemContentChange(value)}
                  placeholder={'Enter patient history notes...'}
                  readOnly={loading}
                  containerDivStyles={{
                    background: 'white',
                    padding: 0,
                    border: '1px solid rgba(67, 56, 202, 0.1)',
                    borderRadius: '12px',
                  }}
                />
                {screeningReportId && (
                  <Box
                    sx={{
                      position: { xs: 'relative', sm: 'absolute' },
                      top: { xs: 'auto', sm: '8px' },
                      right: { xs: 'auto', sm: '8px' },
                      left: { xs: 'auto', sm: 'auto' },
                      zIndex: 10,
                      mt: { xs: 1, sm: 0 },
                      mb: { xs: 1, sm: 0 },
                      display: 'flex',
                      justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    }}
                  >
                    <ImportPatientHistoryButton
                      onImport={handleImportNotes}
                      isImporting={savingRecord}
                      isImported={patientHistoryData?.length > 0}
                      label={isMobile ? "Append" : "Append from screening report"}
                    />
                  </Box>
                )}
              </StyledRTEContainer>
            </Box>
          </CardContent>
        </Card>

        <Stack spacing={2} sx={{ mb: { xs: 1.5, sm: 2 }, maxWidth: '1200px', width: '100%' }}>
          {/* Previous History Entries */}
          {allPatientHistoryData?.length > 0 && (
            <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: '#64748b',
                  mb: { xs: 1, sm: 1.5 },
                  fontSize: { xs: '11px', sm: '13px' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Previous Notes
              </Typography>
              <Stack spacing={{ xs: 1, sm: 1.5 }}>
                {allPatientHistoryData
                  .filter((historyItem) => {
                    // Extra safeguard: ensure the latest note (in patientHistoryData) is never shown in accordions
                    if (patientHistoryData?.id && historyItem?.id === patientHistoryData.id) {
                      return false
                    }
                    // Filter out blank notes
                    const content = historyItem?.content?.content || ''
                    if (!content || typeof content !== 'string') return false
                    // Remove HTML tags and check if there's actual text content
                    const textContent = content
                      .replace(/<[^>]*>/g, '') // Remove HTML tags
                      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
                      .replace(/&amp;/g, '&') // Replace &amp; with &
                      .replace(/&lt;/g, '<') // Replace &lt; with <
                      .replace(/&gt;/g, '>') // Replace &gt; with >
                      .replace(/&quot;/g, '"') // Replace &quot; with "
                      .trim() // Remove leading/trailing whitespace
                    return textContent.length > 0
                  })
                  .map((historyItem, index) => (
                  <Accordion
                    key={historyItem.id || index}
                    defaultExpanded={false}
                    elevation={0}
                    sx={{
                      border: '1px solid rgba(67, 56, 202, 0.1)',
                      borderRadius: { xs: '8px !important', sm: '12px !important' },
                      overflow: 'hidden',
                      background: 'white',
                      '&:before': { display: 'none' },
                      '&:hover': {
                        boxShadow: { xs: '0 1px 4px rgba(67, 56, 202, 0.08)', sm: '0 2px 8px rgba(67, 56, 202, 0.1)' },
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <ExpandMore
                          sx={{
                            color: '#4338ca',
                            fontSize: { xs: '20px', sm: '24px' },
                            '&:hover': {
                              backgroundColor: 'rgba(67, 56, 202, 0.08)',
                              borderRadius: '50%',
                            },
                          }}
                        />
                      }
                      sx={{
                        backgroundColor: '#fafafa',
                        borderRadius: { xs: '8px', sm: '12px' },
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 1, sm: 1.5 },
                        minHeight: { xs: '48px', sm: '56px' },
                        '&.Mui-expanded': {
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0,
                          backgroundColor: '#f8f9fa',
                          minHeight: { xs: '48px', sm: '56px' },
                        },
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: { xs: '8px 0', sm: '12px 0' },
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, width: '100%' }}>
                        <Box
                          sx={{
                            width: { xs: '28px', sm: '32px' },
                            height: { xs: '28px', sm: '32px' },
                            borderRadius: { xs: '6px', sm: '8px' },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                            color: '#4338ca',
                            fontSize: { xs: '11px', sm: '12px' },
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: '#1e293b',
                            fontSize: { xs: '13px', sm: '14px' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {historyItem.createdAt || historyItem.createdTime
                            ? dayjs(historyItem.createdAt || historyItem.createdTime).format('DD MMM YYYY')
                            : `Previous Note ${index + 1}`}
                        </Typography>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 }, pt: { xs: 1.5, sm: 2 } }}>
                      <StyledRTEContainer style={{ position: 'relative' }}>
                        <PatientHistoryTipTapEditor
                          key={`history-item-${historyItem.id || index}`}
                          value={historyItem?.content?.content}
                          placeholder={'No content'}
                          readOnly={true}
                          containerDivStyles={{
                            background: '#fafafa',
                            padding: 0,
                            border: '1px solid rgba(67, 56, 202, 0.08)',
                            borderRadius: '8px',
                          }}
                        />
                      </StyledRTEContainer>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Box>
          )}
          {/* </Box> */}
        </Stack>
        <EmrSectionDataHandler
          hideHeading
          emrSection={patientHistoryNotesSection}
          emrSectionDataList={emrSectionDataList}
          setEmrSectionDataList={setEmrSectionDataList}
          practitionerRole="ROLE_SCREENER"
          runtime={{
            patientId: bookedEventInfo?.patientId,
            eventId: bookedEventInfo?.id,
            // clinicId: clinicId,
            screeningReportId: screeningReportId,
          }}
          excludeSubSectionIds={['FREE_NOTES']}
        />
        {/* <EmrSectionDataHandler
          emrSection={patientHistoryNotesSection}
          singleEmrSectionData={patientHistoryEmrData}
          setSingleEmrSectionData={setPatientHistoryEmrData}
          practitionerRole="ROLE_DOCTOR"
          runtime={{
            patientId: bookedEventInfo?.patientId,
            eventId: bookedEventInfo?.id,
            screeningReportId,
          }}
          excludeSubSectionIds={["'FREE_NOTES'"]}
        /> */}
      </>
    )
  },
)

PatientHistoryNotesDisplay.displayName = 'PatientHistoryNotesDisplay'

export default PatientHistoryNotesDisplay

{
  /* {patientHistoryData.map((historyItem, index) => (
        <Accordion
          key={historyItem.id || index}
          elevation={0}
          sx={{
            border: '1px solid #e0e0e0',
            '&:before': { display: 'none' },
            borderRadius: '4px !important',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                pr: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {index + 1}. {formatDate(historyItem.createdAt || historyItem.createdTimeDate)}
              </Typography>

              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  handleEditClick(historyItem)
                }}
                sx={{ ml: 1 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 0 }}>
            {editingId === historyItem.id ? (
              <Box sx={{ p: 2 }}>
                <RichTextEditor
                  key="screening-report-notes"
                  value={editContent?.content?.content}
                  onChange={(value) => setEditContent(value)}
                  placeholder={'Enter Content'}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSaveEdit(historyItem)}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <>
                {historyItem.content && (
                  <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Content:
                    </Typography>
                    <Typography variant="body1">{historyItem.content}</Typography>
                  </Box>
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>
      ))} */
}
